import { InputNumber, Typography, Alert, Tooltip } from 'antd';
import SeatRing from './SeatRing';

const { Text } = Typography;

/**
 * The ideal room: its size and how many seats go to each group. Whatever
 * isn't reserved is an open seat. Beside it, supply vs. demand per group —
 * how many seats the whole gathering needs from a group against how many of
 * its members are in the pool — so a shortfall shows before any shuffling.
 */
function IdealRoom({ groups, colorOf, size, mix, onSize, onMix, demand, supply, roomCount }) {
    const reservedTotal = groups.reduce((n, g) => n + (mix[g.id] || 0), 0);
    const open = Math.max(0, size - reservedTotal);
    const seats = [
        ...groups.flatMap(g => Array.from({ length: mix[g.id] || 0 }, () => ({
            color: colorOf(g.id), state: 'filled', label: `${g.name || 'Untitled'} seat`,
        }))),
        ...Array.from({ length: open }, () => ({ color: 'var(--border-color-strong)', state: 'open', label: 'Open seat — anyone' })),
    ];

    return (
        <div className="gathering-ideal">
            <div className="gathering-ideal-ring">
                <SeatRing seats={seats} size={150} center={size} />
                <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>
                    {reservedTotal} reserved · {open} open
                </Text>
            </div>
            <div className="gathering-ideal-controls">
                <div className="gathering-ideal-line">
                    <Text strong>People per room</Text>
                    <InputNumber min={2} max={30} value={size} onChange={v => onSize(v || 2)} style={{ width: 90 }} />
                </div>
                {groups.map(g => {
                    const need = demand[g.id] || 0;
                    const have = supply[g.id] || 0;
                    const short = need > have;
                    return (
                        <div key={g.id} className="gathering-ideal-line">
                            <span className="gathering-ideal-group">
                                <span className="gathering-dot" style={{ background: colorOf(g.id) }} />
                                <Text ellipsis style={{ maxWidth: 180 }}>{g.name || 'Untitled'}</Text>
                            </span>
                            <InputNumber
                                min={0}
                                max={size}
                                value={mix[g.id] || 0}
                                onChange={v => onMix(g.id, v || 0)}
                                style={{ width: 90 }}
                                suffix="seats"
                            />
                            <Tooltip title={need
                                ? `${roomCount} room${roomCount === 1 ? '' : 's'} need ${need} from this group; ${have} ${have === 1 ? 'is' : 'are'} in the pool`
                                : 'No reserved seats — members can still take open seats'}>
                                <div className="gathering-supply">
                                    <div className="gathering-supply-track">
                                        <div
                                            className="gathering-supply-fill"
                                            style={{
                                                width: `${need ? Math.min(100, (have / need) * 100) : 0}%`,
                                                background: short ? 'var(--status-warning)' : colorOf(g.id),
                                            }}
                                        />
                                    </div>
                                    <Text type={short ? 'warning' : 'secondary'} style={{ fontSize: 'var(--fs-xs)' }}>
                                        {need ? `${Math.min(have, need)}/${need}` : '—'}
                                    </Text>
                                </div>
                            </Tooltip>
                        </div>
                    );
                })}
                {reservedTotal > size && (
                    <Alert type="error" showIcon title={`${reservedTotal} reserved seats won't fit in a room of ${size}.`} />
                )}
            </div>
        </div>
    );
}

export default IdealRoom;
